import { useEffect, useState } from "react";
import { OWNERS, updatePerson, type UpdatePersonInput } from "../actions";
import { primaryEmail, type Person } from "../data";
import { IcCheck, IcPlus } from "./icons";

/**
 * Editing a contact by hand.
 *
 * Everything the CRM knew about a person arrived from a form, a payment or an
 * import, and none of it could be corrected. A database of 3,893 people with no
 * way to fix a phone number is a database that gets worse every week.
 *
 * ## Sent as a patch, not as the whole record
 *
 * Only fields the user actually changed are sent. Posting the full object back
 * would mean any field this form does not know about — and the person document
 * has around thirty — gets overwritten with whatever the form happened to hold,
 * which is how an edit screen quietly deletes data it never displayed.
 *
 * ## The conflict the server can raise
 *
 * Typing a number that belongs to somebody else is refused, with their name.
 * That is nearly always one of two things: a typo, or two records for one human
 * being. The second is a merge, and the message says so rather than leaving the
 * user to guess why the save failed.
 */

const ROLES: { value: string; label: string }[] = [
  { value: "", label: "לא מוגדר" },
  { value: "father", label: "אב" },
  { value: "mother", label: "אם" },
  { value: "spouse", label: "בן/בת זוג" },
  { value: "child", label: "ילד/ה" },
  { value: "בוגר", label: "בוגר" },
  { value: "other", label: "אחר" },
];

export function EditPersonSheet({
  person,
  onClose,
}: {
  person: Person;
  onClose: () => void;
}) {
  const [name, setName] = useState(person.displayName ?? "");
  const [phones, setPhones] = useState<string[]>(() => {
    const list = (person.phones ?? []).map((p) => p.value ?? "").filter((v) => v !== "");
    return list.length > 0 ? list : [""];
  });
  const [emails, setEmails] = useState<string[]>(() => {
    const list = (person.emails ?? []).map((e) => e.value ?? "").filter((v) => v !== "");
    return list.length > 0 ? list : [""];
  });
  const [street, setStreet] = useState(person.address?.street ?? "");
  const [houseNumber, setHouseNumber] = useState(person.address?.houseNumber ?? "");
  const [city, setCity] = useState(person.address?.city ?? "");
  const [neighborhood, setNeighborhood] = useState(person.address?.neighborhood ?? "");
  const [owners, setOwners] = useState<string[]>(person.owners ?? []);
  const [role, setRole] = useState(person.householdRole ?? "");
  const [gender, setGender] = useState(person.gender ?? "");
  const [birth, setBirth] = useState(person.gregorianBirthDate ?? "");
  const [organization, setOrganization] = useState(person.organization ?? "");
  const [school, setSchool] = useState(person.education?.school ?? "");
  const [grade, setGrade] = useState(person.education?.grade ?? "");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, busy]);

  function toggleOwner(slug: string) {
    setOwners((prev) => (prev.includes(slug) ? prev.filter((o) => o !== slug) : [...prev, slug]));
  }

  async function save() {
    const patch: UpdatePersonInput = { personId: person.id };

    if (name.trim() !== (person.displayName ?? "")) patch.displayName = name.trim();

    const cleanPhones = phones.map((p) => p.trim()).filter((p) => p !== "");
    const wasPhones = (person.phones ?? []).map((p) => p.value ?? "").filter((v) => v !== "");
    if (cleanPhones.join("|") !== wasPhones.join("|")) patch.phones = cleanPhones;

    const cleanEmails = emails.map((e) => e.trim()).filter((e) => e !== "");
    const wasEmails = (person.emails ?? []).map((e) => e.value ?? "").filter((v) => v !== "");
    if (cleanEmails.join("|") !== wasEmails.join("|")) patch.emails = cleanEmails;

    const addr = {
      street: street.trim() || null,
      houseNumber: houseNumber.trim() || null,
      city: city.trim() || null,
      neighborhood: neighborhood.trim() || null,
    };
    const wasAddr = {
      street: person.address?.street ?? null,
      houseNumber: person.address?.houseNumber ?? null,
      city: person.address?.city ?? null,
      neighborhood: person.address?.neighborhood ?? null,
    };
    if (JSON.stringify(addr) !== JSON.stringify(wasAddr)) {
      patch.address = Object.values(addr).every((v) => v === null) ? null : addr;
    }

    if (owners.join("|") !== (person.owners ?? []).join("|")) patch.owners = owners;
    if (role !== (person.householdRole ?? "")) patch.householdRole = role || null;
    if (gender !== (person.gender ?? "")) {
      patch.gender = gender === "male" || gender === "female" ? gender : null;
    }
    if (birth !== (person.gregorianBirthDate ?? "")) patch.gregorianBirthDate = birth || null;
    if (organization.trim() !== (person.organization ?? "")) {
      patch.organization = organization.trim() || null;
    }
    const edu = { school: school.trim() || null, grade: grade.trim() || null };
    const wasEdu = {
      school: person.education?.school ?? null,
      grade: person.education?.grade ?? null,
    };
    if (JSON.stringify(edu) !== JSON.stringify(wasEdu)) {
      patch.education = edu.school === null && edu.grade === null ? null : edu;
    }

    if (Object.keys(patch).length === 1) {
      onClose();
      return;
    }

    setBusy(true);
    setErr(null);
    try {
      await updatePerson(patch);
      setDone(true);
      setTimeout(onClose, 800);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="scrim" onClick={busy ? undefined : onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label="עריכת איש קשר">
        <div className="sheet-grip" />

        {done ? (
          <div className="qa-done">
            <div className="qa-done-mark">
              <IcCheck size={26} />
            </div>
            <h3>נשמר</h3>
            <p>עדכנו את הפרטים.</p>
          </div>
        ) : (
          <>
            <div className="sheet-head">
              <h2>עריכת פרטים</h2>
            </div>

            <div className="form-field">
              <span className="form-label">שם</span>
              <input
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
              />
            </div>

            <MultiField
              label="טלפון"
              values={phones}
              onChange={setPhones}
              placeholder="05X-XXXXXXX"
              type="tel"
              disabled={busy}
              hint="למספר הראשון מתקשרים. כששומרים מספר חדש, נזהה לפיו גם טפסים שיגיעו בהמשך ונחבר אותם לאיש הקשר הנכון."
            />

            <MultiField
              label="מייל"
              values={emails}
              onChange={setEmails}
              placeholder="name@example.com"
              type="email"
              disabled={busy}
            />

            <div className="form-field">
              <span className="form-label">שיוך</span>
              <div className="chips">
                {OWNERS.map((o) => (
                  <button
                    key={o.slug}
                    type="button"
                    className={owners.includes(o.slug) ? "tagchip tagchip-on" : "tagchip"}
                    aria-pressed={owners.includes(o.slug)}
                    onClick={() => toggleOwner(o.slug)}
                    disabled={busy}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <span className="form-hint">אפשר לשייך לשניכם, או לאף אחד.</span>
            </div>

            <div className="form-field">
              <span className="form-label">כתובת</span>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  className="form-input"
                  style={{ flex: 3 }}
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="רחוב"
                  disabled={busy}
                />
                <input
                  className="form-input"
                  style={{ flex: 1 }}
                  value={houseNumber}
                  onChange={(e) => setHouseNumber(e.target.value)}
                  placeholder="מס׳"
                  disabled={busy}
                />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <input
                  className="form-input"
                  value={neighborhood}
                  onChange={(e) => setNeighborhood(e.target.value)}
                  placeholder="שכונה"
                  disabled={busy}
                />
                <input
                  className="form-input"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="עיר"
                  disabled={busy}
                />
              </div>
            </div>

            <div className="form-field">
              <span className="form-label">תפקיד במשפחה</span>
              <select
                className="form-input"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={busy}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <span className="form-label">מגדר</span>
              <div className="chips">
                {[
                  { v: "", l: "לא מוגדר" },
                  { v: "male", l: "גבר" },
                  { v: "female", l: "אישה" },
                ].map((g) => (
                  <button
                    key={g.v}
                    type="button"
                    className={gender === g.v ? "tagchip tagchip-on" : "tagchip"}
                    onClick={() => setGender(g.v)}
                    disabled={busy}
                  >
                    {g.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-field">
              <span className="form-label">תאריך לידה</span>
              <input
                className="form-input"
                type="date"
                value={birth}
                onChange={(e) => setBirth(e.target.value)}
                disabled={busy}
              />
              <span className="form-hint">את התאריך העברי נחשב מזה אוטומטית.</span>
            </div>

            <div className="form-field">
              <span className="form-label">עסק</span>
              <input
                className="form-input"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                placeholder="שם העסק, אם יש"
                disabled={busy}
              />
            </div>

            <div className="form-field">
              <span className="form-label">מסגרת חינוכית</span>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  className="form-input"
                  style={{ flex: 2 }}
                  value={school}
                  onChange={(e) => setSchool(e.target.value)}
                  placeholder="גן / בית ספר"
                  disabled={busy}
                />
                <input
                  className="form-input"
                  style={{ flex: 1 }}
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  placeholder="כיתה"
                  disabled={busy}
                />
              </div>
            </div>

            {err && <div className="form-error">{err}</div>}

            <div className="btn-row">
              <button className="btn btn-primary" onClick={save} disabled={busy}>
                {busy ? "שומרים…" : "שמרו"}
              </button>
              <button className="btn" onClick={onClose} disabled={busy}>
                ביטול
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/**
 * A list of one kind of value — several phone numbers, several addresses.
 *
 * A single input would have forced a choice between losing the second number and
 * asking somebody to type comma-separated values into a field, and people do
 * genuinely have two numbers.
 */
function MultiField({
  label,
  values,
  onChange,
  placeholder,
  type,
  disabled,
  hint,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
  type: string;
  disabled: boolean;
  hint?: string;
}) {
  return (
    <div className="form-field">
      <span className="form-label">{label}</span>
      {values.map((v, i) => (
        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
          <input
            className="form-input"
            type={type}
            dir="ltr"
            value={v}
            onChange={(e) => onChange(values.map((x, j) => (j === i ? e.target.value : x)))}
            placeholder={placeholder}
            disabled={disabled}
          />
          {values.length > 1 && (
            <button
              type="button"
              onClick={() => onChange(values.filter((_, j) => j !== i))}
              disabled={disabled}
              aria-label="הסרה"
              style={{ padding: "0 10px", color: "var(--red)", fontWeight: 700 }}
            >
              ×
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        className="tagchip"
        onClick={() => onChange([...values, ""])}
        disabled={disabled}
      >
        <IcPlus size={12} /> עוד {label}
      </button>
      {hint && <span className="form-hint">{hint}</span>}
    </div>
  );
}

/** Kept for the caller that wants to show what is on file before opening this. */
export const currentEmail = primaryEmail;
